import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { z } from 'zod';

import { useIsAuthenticated } from '../../auth/authentication';
import { zodDatetime } from '../../utils/zodHelpers';
import { useAxiosClient } from '../axiosClient';
import { qhpForListDisplaySchema } from '../sharedSchemas/misc';

const locationInfoSchema = z.object({
    lat: z.string(),
    lng: z.string(),
    timestamp: zodDatetime(),
});

const qhpWithLocationInfoSchema = z.object({
    qhp: qhpForListDisplaySchema,
    location_info: locationInfoSchema,
});

const responseSchema = z.object({
    live_locations: z.array(qhpWithLocationInfoSchema),
});

type ResponseSchemaInput = z.input<typeof responseSchema>;

export type QhpWithLocationInfo = z.infer<typeof qhpWithLocationInfoSchema>;

const EMPTY_LOCATIONS: QhpWithLocationInfo[] = [];

/**
 * Fetches live clinician locations. This hook is designed to fail gracefully:
 * - Returns an empty array while loading or on error
 * - Does not suspend or throw errors (to avoid crashing the page)
 * - Errors are logged to Sentry but no toast is shown (since this polls every 30s)
 */
export const useLiveLocations = (): QhpWithLocationInfo[] => {
    const axiosClient = useAxiosClient();
    const isAuthenticated = useIsAuthenticated();

    const { data: rawData } = useQuery<ResponseSchemaInput | null>({
        queryKey: ['get', '/live-locations'],
        queryFn: async ({ signal }): Promise<ResponseSchemaInput | null> => {
            try {
                const response = await axiosClient({
                    method: 'get',
                    url: '/live-locations',
                    signal,
                });
                return response.data as ResponseSchemaInput;
            } catch (error) {
                Sentry.captureException(error, {
                    tags: { source: 'useLiveLocations' },
                });
                return null;
            }
        },
        enabled: isAuthenticated,
        refetchInterval: 30_000,
        retry: false,
    });

    return useMemo(() => {
        if (!rawData) return EMPTY_LOCATIONS;
        try {
            return responseSchema.parse(rawData).live_locations;
        } catch {
            return EMPTY_LOCATIONS;
        }
    }, [rawData]);
};
